import { Minus } from 'lucide-react';
import type { Category } from '../../api/categories';
import { MultiSearchableSelect } from '../ui/MultiSearchableSelect';
import { cn } from '../ui/utils';

interface CategorySelectorProps {
  categories: Category[];
  selectedCategories: Category[];
  onCategoryChange: (categories: Category[]) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

export function CategorySelector({
  categories,
  selectedCategories,
  onCategoryChange,
  disabled = false,
  className = '',
  placeholder = 'Select categories...',
}: CategorySelectorProps) {

  // Convert categories to the format expected by MultiSearchableSelect
  const categoryOptions = categories.map(category => ({
    value: category.id,
    label: category.name,
    isActive: category.isActive
  }));

  // Convert selected categories to an array of IDs
  const selectedValues = selectedCategories.map(cat => cat.id);

  // Handle selection changes
  const handleChange = (values: string[]) => {
    // Map the selected IDs back to Category objects
    const selected = values.map(id => categories.find(cat => cat.id === id)).filter(Boolean) as Category[];
    onCategoryChange(selected);
  };

  return (
    <div className={cn('space-y-2', className)}>
      <label className="block text-sm font-medium text-gray-300 mb-2">
        Categories
      </label>
      <div>
        <MultiSearchableSelect
          options={categoryOptions}
          values={selectedValues}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          searchPlaceholder="Search categories..."
          emptyText="No categories found"
          className="w-full"
        />
      </div>
    </div>
  );
}

export function CategoryDisplay({ categories }: { categories: Category[] }) {
  if (!categories || categories.length === 0) {
    return <span className="text-xs text-slate-400"><Minus className='w-5 h-5' /></span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {categories?.slice(0, 2).map((cat) => (
        <span key={cat?.id || cat?.name} className="inline-block px-2 py-1 rounded-md bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 text-xs">
          {cat?.name || "Unknown"}
        </span>
      ))}

      {categories?.length > 2 && (
        <span className="inline-block px-2 py-1 rounded-md bg-white/5 text-slate-400 text-xs">
          +{categories?.length - 2}
        </span>
      )}
    </div>
  );
}
